import * as React from 'react';
import { IoChevronForward, IoChevronDown } from 'react-icons/io5';
import abbreviateNumber from 'utils/shared/number/abbreviateNumber';
import { HeatScore } from 'components/Score';
import { CallPutText, CallPutBackgroundMobile } from 'components/Table';
import { ChainRow } from 'utils/shared/trades/groupTradesIntoChain';
import type { FlowMetricMode } from 'utils/shared/trades/presets';

import { CardWrapper, CardMutedText, ChildCardWrapper } from './styles';

interface Props {
  row: ChainRow;
  isExpanded: boolean;
  onToggle: (key: string) => void;
  metricMode?: FlowMetricMode;
}

const MobileChainCard: React.FC<Props> = ({ row, isExpanded, onToggle, metricMode = 'total' }) => {
  const cpLabel = row.cp === 'Call' || row.cp === 'C' ? 'CALL' : 'PUT';
  const isNetMode = metricMode === 'net';
  const displayPremium = isNetMode ? row.netPremium : row.totalPremium;
  const displaySize = isNetMode ? row.netSize : row.totalSize;

  return (
    <div className="pt-1">
      <CardWrapper className="p-4">
        <div className="flex w-full">
          {/* Left column */}
          <div className="flex w-1/2 flex-col pr-1 text-xs leading-none">
            <div className="mb-2 flex items-center gap-1">
              <CardMutedText>{row.date}</CardMutedText>
              <button
                className="cursor-pointer text-gray-400"
                onClick={() => onToggle(row.key)}
                aria-expanded={isExpanded}
                aria-label={isExpanded ? 'Collapse chain' : 'Expand chain'}
              >
                {isExpanded ? (
                  <IoChevronDown className="h-3.5 w-3.5" />
                ) : (
                  <IoChevronForward className="h-3.5 w-3.5" />
                )}
              </button>
            </div>
            <div className="flex items-center">
              <CallPutBackgroundMobile type={row.cp}>{row.ticker}</CallPutBackgroundMobile>
              <div className="ml-2 font-bold">${abbreviateNumber(displayPremium)}</div>
            </div>
            <div className="mt-2 flex flex-col">
              <div className="flex items-center leading-none">
                <CallPutText className="inline" type={row.cp}>
                  {cpLabel}
                </CallPutText>{' '}
                <span className="ml-1">${row.strike}</span>
              </div>
              <div className="mt-2 leading-none">
                <span>
                  {displaySize.toLocaleString()} {isNetMode ? 'net size' : 'size'}
                </span>
                <span className="ml-2 text-gray-400">{row.count} trades</span>
              </div>
              <div className="mt-2 flex items-center">
                <div className="mr-4 shrink-0 text-xs">Heat Score</div>
                <div className="w-full pt-0.5" style={{ maxWidth: '64px' }}>
                  <HeatScore scoreWidth={row.avgHeatScore} />
                </div>
              </div>
            </div>
          </div>
          {/* Right column - labeled key-value pairs */}
          <div className="flex w-1/2 flex-col justify-between space-y-1 pl-1 text-xs leading-none">
            <div className="flex items-center justify-between">
              <div>Expiration</div>
              <div>{row.expiry}</div>
            </div>
            <div className="flex items-center justify-between">
              <div>Strike</div>
              <div>${row.strike}</div>
            </div>
            <div className="flex items-center justify-between">
              <div>Avg Spot</div>
              <div>${row.avgSpot?.toFixed(2) ?? '—'}</div>
            </div>
            <div className="flex items-center justify-between">
              <div>Avg OTM</div>
              <div>{Math.round(row.avgOtmPercent)}%</div>
            </div>
            <div className="flex items-center justify-between">
              <div>Open Interest</div>
              <div>{row.openInterest.toLocaleString()}</div>
            </div>
            <div className="flex items-center justify-between">
              <div>Avg IV</div>
              <div>{Math.round(row.avgImpliedVol * 100)}%</div>
            </div>
          </div>
        </div>
      </CardWrapper>
      {/* Expanded child trade cards */}
      {isExpanded && (
        <div className="border-l-2 border-t border-gray-700 border-l-brand-primary">
          {row.trades.map((trade) => (
            <ChildCardWrapper key={trade.id} className="px-4 py-2.5">
              <div className="flex w-full">
                {/* Child left column */}
                <div className="flex w-1/2 flex-col pr-1 text-xs leading-none">
                  <div className="mb-1.5">
                    <CardMutedText>
                      {trade.date} {trade.time} {trade.timeSubscript}
                    </CardMutedText>
                  </div>
                  <div className="flex items-center">
                    <CallPutText type={trade.cp || ''}>
                      <span className="font-semibold">{trade.ticker}</span>
                    </CallPutText>
                    <div className="ml-2 font-bold">${abbreviateNumber(trade.premium)}</div>
                  </div>
                  <div className="mt-1.5 flex items-center leading-none">
                    {trade.size?.toLocaleString()} @ ${trade.price?.toFixed(2) ?? '—'}{' '}
                    <span className="ml-1 text-gray-400">{trade.tradeDirection}</span>
                  </div>
                  <div className="mt-1.5 leading-none">
                    <CallPutText className="inline" type={trade.cp || ''}>
                      {trade.cp?.toUpperCase()}
                    </CallPutText>{' '}
                    <span>{trade.orderType}</span>
                  </div>
                </div>
                {/* Child right column */}
                <div className="flex w-1/2 flex-col justify-between space-y-1 pl-1 text-xs leading-none">
                  <div className="flex items-center justify-between">
                    <div>Spot</div>
                    <div>${trade.spot?.toFixed(2) ?? '—'}</div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>Strike</div>
                    <div>${trade.strike}</div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>OTM</div>
                    <div>{trade.otmPercent != null ? `${Math.round(trade.otmPercent)}%` : '—'}</div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>OI</div>
                    <div>{trade.openInterest?.toLocaleString() ?? '—'}</div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>IV</div>
                    <div>
                      {trade.impliedVol != null ? `${Math.round(trade.impliedVol * 100)}%` : '—'}
                    </div>
                  </div>
                </div>
              </div>
            </ChildCardWrapper>
          ))}
        </div>
      )}
    </div>
  );
};

export default MobileChainCard;
